// src/pages/patientPages/Wastebasket/Wastebasket.jsx
import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Input, Select, Tooltip } from "antd";
import {
  DeleteOutlined,
  UndoOutlined,
  SearchOutlined,
  DownOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AppLayout from "../../../navigation/Layout/Layout";
import useTrash from "../../hooks/useTrash";
import "./Wastebasket.css";

const { Option } = Select;

export default function Wastebasket() {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || "").startsWith("ar");

  const {
    filteredData,
    loading,
    selectedRowKeys,
    selectedCount,
    searchText,
    sortOrder,
    filterLocation,
    isRestoring,
    isDeleting,
    isEmptying,
    isTrashEmpty,
    hasSelection,
    isAnyOperationPending,
    handleRestoreDocument,
    handlePermanentlyDeleteDocument,
    handleBulkRestore,
    handleBulkDelete,
    handleEmptyTrash,
    handleSelectionChange,
    handleClearSelection,
    handleSearchChange,
    handleSortOrderChange,
    handleFilterLocationChange,
    refreshTrash,
  } = useTrash();

  // ✅ Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnyOperationPending) refreshTrash();
    }, 30000);
    return () => clearInterval(interval);
  }, [isAnyOperationPending, refreshTrash]);

  // ✅ Local modal states
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [bulkRestoreOpen, setBulkRestoreOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  /* ========== Sort icons ========== */
  const ArrowUp = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M5 12L12 5L19 12" stroke="#8EBB82" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 19L12 5" stroke="#8EBB82" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const ArrowDown = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M5 12L12 19L19 12" stroke="#F9B781" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 5L12 19" stroke="#F9B781" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  /* ========== Single actions ========== */
  const handleRestore = (record) => {
    setItemToRestore(record);
    setRestoreOpen(true);
  };

  const confirmRestore = async () => {
    if (!itemToRestore) return;
    await handleRestoreDocument(itemToRestore);
    setRestoreOpen(false);
    setItemToRestore(null);
  };

  const handleDelete = (record) => {
    setItemToDelete(record);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    await handlePermanentlyDeleteDocument(itemToDelete);
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  /* ========== Bulk actions ========== */
  const onBulkRestore = () => {
    if (!hasSelection) return;
    setBulkRestoreOpen(true);
  };

  const confirmBulkRestore = async () => {
    await handleBulkRestore();
    setBulkRestoreOpen(false);
  };

  const onBulkDelete = () => {
    if (!hasSelection) return;
    setBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async () => {
    await handleBulkDelete();
    setBulkDeleteOpen(false);
  };

  /* ========== Empty trash ========== */
  const onEmptyTrash = () => {
    if (isTrashEmpty) return;
    setEmptyOpen(true);
  };

  const confirmEmptyTrash = async () => {
    await handleEmptyTrash();
    setEmptyOpen(false);
  };

  /* ========== Table columns ========== */
  const columns = [
    {
      title: t("wastebasket.table.columns.documentName"),
      dataIndex: "fileName",
      key: "fileName",
      width: "26%",
      render: (text) => <span className="wb-filename">{text}</span>,
    },
    {
      title: t("wastebasket.table.columns.originalLocation"),
      dataIndex: "location",
      key: "location",
      width: "24%",
      render: (text) => <span className="wb-location">{text}</span>,
    },
    {
      title: t("wastebasket.table.columns.dateDeleted"),
      dataIndex: "deletedDate",
      key: "deletedDate",
      width: "16%",
    },
    {
      title: t("wastebasket.table.columns.size"),
      dataIndex: "size",
      key: "size",
      width: "10%",
    },
    {
      title: t("wastebasket.table.columns.itemType"),
      dataIndex: "type",
      key: "type",
      width: "14%",
    },
    {
      title: t("wastebasket.table.columns.actions"),
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <div className="wb-actions">
          <button
            className="wb-action-btn wb-action-btn--restore"
            onClick={() => handleRestore(record)}
            title={t("wastebasket.actions.restore")}
            type="button"
            disabled={isAnyOperationPending}
          >
            <UndoOutlined />
          </button>
          <button
            className="wb-action-btn wb-action-btn--delete"
            onClick={() => handleDelete(record)}
            title={t("wastebasket.actions.deletePermanently")}
            type="button"
            disabled={isAnyOperationPending}
          >
            <DeleteOutlined />
          </button>
        </div>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectionChange,
  };

  return (
    <AppLayout>
      <div className="wastebasket-page" dir={isRTL ? "rtl" : "ltr"}>
        <div className="wb-container">
          <div className="wb-header-toolbar">
            <div className="wb-header-section">
              <h1 className="wb-main-title">{t("wastebasket.title")}</h1>
              <p className="wb-subtitle">{t("wastebasket.subtitle")}</p>

              {hasSelection && (
                <div className="wb-bulkbar">
                  <span className="wb-bulkbar-label">
                    {t("wastebasket.bulkbar.selectedItems", { count: selectedCount })}
                  </span>

                  <div className="wb-bulkbar-actions">
                    <Button
                      className="wb-bulk-btn wb-bulk-btn--restore"
                      onClick={onBulkRestore}
                      disabled={isAnyOperationPending}
                      loading={isRestoring}
                      icon={<UndoOutlined />}
                    >
                    </Button>

                    <Button
                      className="wb-bulk-btn wb-bulk-btn--delete"
                      danger
                      onClick={onBulkDelete}
                      disabled={isAnyOperationPending}
                      loading={isDeleting}
                      icon={<DeleteOutlined />}
                    >
                    </Button>

                    <Tooltip title={t("wastebasket.bulkbar.clearSelection")}>
                      <button
                        type="button"
                        className="wb-bulk-icon wb-bulk-icon--clear"
                        onClick={handleClearSelection}
                      >
                        <CloseOutlined />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>

            <div className="wb-toolbar">
              <div className="wb-toolbar-left">
                <span className="wb-sort-label">{t("wastebasket.toolbar.sortBy")}</span>

                <button
                  className="wb-sort-btn"
                  type="button"
                  onClick={handleSortOrderChange}
                  disabled={isAnyOperationPending}
                >
                  {sortOrder === "asc" ? <ArrowUp /> : <ArrowDown />}
                </button>

                <Select
                  className="wb-filter-select"
                  value={filterLocation}
                  onChange={handleFilterLocationChange}
                  suffixIcon={<DownOutlined />}
                  disabled={isAnyOperationPending}
                  placeholder="Filter by type"
                >
                  <Option value="all">{t("wastebasket.toolbar.filters.allDocuments", "All Documents")}</Option>
                  <Option value="bilan">{t("wastebasket.toolbar.filters.biochemistryTests", "Biochemistry Tests")}</Option>
                  <Option value="irm">{t("wastebasket.toolbar.filters.mriScans", "MRI Scans")}</Option>
                  <Option value="echographie">{t("wastebasket.toolbar.filters.medicalUltrasound", "Medical Ultrasound")}</Option>
                  <Option value="rapport">{t("wastebasket.toolbar.filters.radiography", "Radiography")}</Option>
                  <Option value="ordonnance">{t("wastebasket.toolbar.filters.medicalPrescription", "Medical Prescription")}</Option>
                  <Option value="compte_rendu">{t("wastebasket.toolbar.filters.medicalReports", "Medical Reports")}</Option>
                  <Option value="autre">{t("wastebasket.toolbar.filters.others", "Others")}</Option>
                </Select>

                <Input
                  className="wb-search-input"
                  placeholder={t("wastebasket.toolbar.searchPlaceholder")}
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  allowClear
                  disabled={isAnyOperationPending}
                />
              </div>

              <Button
                className="wb-empty-btn"
                danger
                onClick={onEmptyTrash}
                disabled={isTrashEmpty || isAnyOperationPending}
                loading={isEmptying}
              >
                {t("wastebasket.toolbar.emptyButton")}
                <DeleteOutlined />
              </Button>
            </div>
          </div>

          <div className="wb-table-wrapper">
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredData}
              loading={loading || isAnyOperationPending}
              pagination={false}
              showHeader
              // ✅ IMPORTANT : rowKey stable basé sur trashId
              rowKey={(record) => record.trashId || record._id || record.id || record.key}
              locale={{
                emptyText: (
                  <div className="wb-empty-state">
                    <DeleteOutlined className="wb-empty-icon" />
                    <p>{t("wastebasket.table.emptyState")}</p>
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* ✅ MODAL RESTORE SINGLE */}
      <Modal
        open={restoreOpen}
        onCancel={() => {
          setRestoreOpen(false);
          setItemToRestore(null);
        }}
        footer={null}
        centered
        destroyOnClose
        wrapClassName="wb-restore-single-modal"
        closeIcon={<span className="wb-restore-single-close">×</span>}
      >
        <div className="wb-restore-single">
          <div className="wb-restore-single-icon">
            <UndoOutlined />
          </div>
          <h3 className="wb-restore-single-title">{t("wastebasket.modals.restoreSingle.title")}</h3>
          <p className="wb-restore-single-description">
            {t("wastebasket.modals.restoreSingle.description")}
          </p>
          <button
            type="button"
            className="wb-restore-single-confirm-btn"
            onClick={confirmRestore}
            disabled={isRestoring}
          >
            {t("wastebasket.modals.restoreSingle.confirmButton")}
          </button>
        </div>
      </Modal>

      {/* ✅ MODAL DELETE SINGLE */}
      <Modal
        open={deleteOpen}
        onCancel={() => {
          setDeleteOpen(false);
          setItemToDelete(null);
        }}
        footer={null}
        centered
        destroyOnClose
        wrapClassName="wb-delete-single-modal"
        closeIcon={<span className="wb-delete-single-close">×</span>}
      >
        <div className="wb-delete-single">
          <div className="wb-delete-single-icon">
            <DeleteOutlined />
          </div>
          <h3 className="wb-delete-single-title">{t("wastebasket.modals.deleteSingle.title")}</h3>
          <p className="wb-delete-single-description">
            {t("wastebasket.modals.deleteSingle.description")}
          </p>
          <button
            type="button"
            className="wb-delete-single-confirm-btn"
            onClick={confirmDelete}
            disabled={isDeleting}
          >
            {t("wastebasket.modals.deleteSingle.confirmButton")}
          </button>
        </div>
      </Modal>

      {/* ✅ MODAL BULK RESTORE */}
      <Modal
        open={bulkRestoreOpen}
        onCancel={() => setBulkRestoreOpen(false)}
        footer={null}
        centered
        destroyOnClose
        wrapClassName="wb-restore-single-modal"
        closeIcon={<span className="wb-restore-single-close">×</span>}
      >
        <div className="wb-restore-single">
          <div className="wb-restore-single-icon">
            <UndoOutlined />
          </div>
          <h3 className="wb-restore-single-title">{t("wastebasket.modals.bulkRestore.title")}</h3>
          <p className="wb-restore-single-description">
            {t("wastebasket.modals.bulkRestore.description", { count: selectedCount })}
          </p>
          <button
            type="button"
            className="wb-restore-single-confirm-btn"
            onClick={confirmBulkRestore}
            disabled={isRestoring}
          >
            {t("wastebasket.modals.bulkRestore.confirmButton")}
          </button>
        </div>
      </Modal>

      {/* ✅ MODAL BULK DELETE */}
      <Modal
        open={bulkDeleteOpen}
        onCancel={() => setBulkDeleteOpen(false)}
        footer={null}
        centered
        destroyOnClose
        wrapClassName="wb-delete-single-modal"
        closeIcon={<span className="wb-delete-single-close">×</span>}
      >
        <div className="wb-delete-single">
          <div className="wb-delete-single-icon">
            <DeleteOutlined />
          </div>
          <h3 className="wb-delete-single-title">{t("wastebasket.modals.bulkDelete.title")}</h3>
          <p className="wb-delete-single-description">
            {t("wastebasket.modals.bulkDelete.description", { count: selectedCount })}
          </p>
          <button
            type="button"
            className="wb-delete-single-confirm-btn"
            onClick={confirmBulkDelete}
            disabled={isDeleting}
          >
            {t("wastebasket.modals.bulkDelete.confirmButton")}
          </button>
        </div>
      </Modal>

      {/* ✅ MODAL EMPTY TRASH */}
      <Modal
        open={emptyOpen}
        onCancel={() => setEmptyOpen(false)}
        footer={null}
        centered
        destroyOnClose
        wrapClassName="wb-empty-bin-modal"
        closeIcon={<span className="wb-empty-bin-close">×</span>}
      >
        <div className="wb-empty-bin">
          <div className="wb-empty-bin-icon">
            <DeleteOutlined />
          </div>
          <h3 className="wb-empty-bin-title">{t("wastebasket.modals.emptyBin.title")}</h3>
          <p className="wb-empty-bin-description">
            {t("wastebasket.modals.emptyBin.description")}
          </p>
          <button
            type="button"
            className="wb-empty-bin-confirm-btn"
            onClick={confirmEmptyTrash}
            disabled={isEmptying}
          >
            {t("wastebasket.modals.emptyBin.confirmButton")}
          </button>
        </div>
      </Modal>
    </AppLayout>
  );
}